/* MODIFICATION PROVIDED BY SEARCHANISE TEAM. WITH ANY QUESTIONS, CONTACT US AT feedback@searchanise.io

INSTRUCTIONS

Modification adds banners to the top of the product grid on specified collections.
The banner information includes the target page link, heading text, secondary text, button text, and the list of the collections the banner is shown for.

You can add, remove and modify banners and their information.

The information about the banners is included in the banners list, it has the following structure: 

let banners = {
    "pages/target-page-link1": {
        ...
    }, 
    "pages/target-page-link2": {
        ...
    }
}

When adding new banners, please be sure to follow these formatting rules: 
1. New banner objects should be put between the banners curly braces {}
2. Individual banners should be divided by a comma.

Here is the structure of an individual banner inside the banners list:

"pages/target-page-link": {
    textMain: "Replace with the banner heading text",
    textSec: "Replace with the banner secondary text",
    button: "Replace with the banner button text",
    collections: ["/collections/collection-handle1", "/collections/collection-handle2"]
}

For banners with multiple links, use the multiLink structure:

"multi-heater-tools": {
    textMain: "Replace with the banner heading text",
    textSec: "Replace with the banner secondary text",
    multiLink: true,
    links: [
        { url: "/pages/link1", text: "Button 1" },
        { url: "/pages/link2", text: "Button 2" }
    ],
    collections: ["/collections/collection-handle1"]
}

Here are the formatting rules to modify the information about the banner: 
1. Put all texts that you add (page link, texts, collection links) in double quote marks. 
2. If you don't want to fill out a particular field, for example, textSec, you can leave empty quote marks instead " ".
3. Each line should end with a comma, except the last one.
4. Collection links should be put between square brackets.
5. Collection links should be divided by a comma.

Aside from the banners variable, you will find the isEnabled variable in the settings section. 
By default, it is set to true, if you need to disable the modification, set it to false, so it look like that:

let isEnabled = false;

*/

// SETTINGS START

// CSS STYLES
const bannerStyles = `
<style>
/* Modern, premium banner styling */
.snize-custom-banner {
    display: flex !important;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f8fafb 0%, #ffffff 100%) !important;
    border: 1px solid #e0e4e8 !important;
    border-radius: 12px;
    padding: 28px 60px; /* REDUCED from 42px to 28px */
    margin: 0 0 36px 0 !important;
    width: 100%;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    gap: 48px;
    min-height: 100px; /* REDUCED from 140px */
}

/* Multi-link banner specific styles */
.snize-custom-banner.multi-link-banner {
    flex-wrap: wrap;
    gap: 24px;
}

.snize-custom-banner.multi-link-banner .snize-banner-text-container {
    flex: 1 1 100%;
    text-align: center;
    margin-bottom: 8px;
}

.snize-custom-banner.multi-link-banner .snize-banner-links-container {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
}

/* Subtle gradient accent */
.snize-custom-banner::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #0095ff 0%, #0085e6 50%, #0095ff 100%);
    background-size: 200% 100%;
    animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
}

/* Add subtle pattern overlay */
.snize-custom-banner::after {
    content: '';
    position: absolute;
    top: 0;
    right: -20%;
    width: 40%;
    height: 100%;
    background: radial-gradient(circle at center, rgba(0, 149, 255, 0.03) 0%, transparent 70%);
    pointer-events: none;
}

.snize-custom-banner:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
}

/* Text container */
.snize-custom-banner .snize-banner-text-container {
    display: flex;
    flex-direction: column;
    gap: 8px; /* REDUCED from 10px */
    flex: 0 1 auto;
    padding-left: 0;
    position: relative;
    z-index: 1;
}

.snize-custom-banner .snize-banner-text-main {
    font-size: 20px; /* REDUCED from 22px */
    font-weight: 700;
    color: #0f172a;
    line-height: 1.25;
    margin: 0;
    display: block;
    letter-spacing: -0.4px;
}

.snize-custom-banner .snize-banner-text-sec {
    font-size: 15px; /* REDUCED from 16px */
    color: #64748b;
    line-height: 1.5;
    margin: 0;
    display: block;
    font-weight: 450;
    letter-spacing: -0.1px;
}

.snize-custom-banner .snize-banner-link {
    background: linear-gradient(135deg, #0095ff 0%, #0085e6 100%);
    color: white !important;
    font-size: 14px; /* REDUCED from 15px */
    font-weight: 600;
    padding: 12px 28px; /* REDUCED from 14px 32px */
    border-radius: 8px;
    text-decoration: none !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(0, 149, 255, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.2);
    border: none;
    position: relative;
    overflow: hidden;
    letter-spacing: 0.3px;
    text-transform: none;
}

/* Button arrow */
.snize-custom-banner .snize-banner-link::after {
    content: '→';
    font-size: 18px;
    transition: transform 0.3s ease;
    margin-left: 4px;
}

/* Button shine effect */
.snize-custom-banner .snize-banner-link::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
    transform: rotate(45deg) translateX(-100%);
    transition: transform 0.6s;
}

.snize-custom-banner .snize-banner-link:hover {
    background: linear-gradient(135deg, #0085e6 0%, #0075d6 100%);
    box-shadow: 0 6px 20px rgba(0, 149, 255, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
}

.snize-custom-banner .snize-banner-link:hover::after {
    transform: translateX(4px);
}

.snize-custom-banner .snize-banner-link:hover::before {
    transform: rotate(45deg) translateX(100%);
}

.snize-custom-banner .snize-banner-link:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 149, 255, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

/* Tablet styles */
@media (max-width: 1024px) and (min-width: 769px) {
    .snize-custom-banner {
        padding: 38px 32px 42px 32px;
        gap: 32px;
        min-height: 120px;
    }
    
    .snize-custom-banner .snize-banner-text-container {
        padding-left: 0;
    }
    
    .snize-custom-banner .snize-banner-text-main {
        font-size: 20px;
    }
    
    .snize-custom-banner .snize-banner-text-sec {
        font-size: 15px;
    }
    
    .snize-custom-banner .snize-banner-link {
        padding: 12px 28px;
        font-size: 14px;
    }
    
    .snize-custom-banner.multi-link-banner .snize-banner-links-container {
        gap: 12px;
    }
}

/* Mobile styles */
@media (max-width: 768px) {
    .snize-custom-banner {
        display: flex !important;
        flex-direction: column;
        align-items: center !important;
        justify-content: center !important;
        padding: 20px 10px !important;
        margin: 0 auto 24px auto !important;
        border-radius: 10px;
        min-height: auto;
        text-align: center !important;
        width: calc(100% - 20px) !important;
        max-width: 100%;
        box-sizing: border-box;
        gap: 16px;
        overflow: visible !important;
    }
    
    .snize-custom-banner::after {
        display: none !important;
    }
    
    .snize-custom-banner .snize-banner-text-container {
        display: block;
        text-align: center !important;
        padding: 0 !important;
        margin: 0 !important;
        width: 100%;
    }
    
    .snize-custom-banner .snize-banner-text-main {
        display: block;
        font-size: 18px;
        text-align: center !important;
        line-height: 1.3;
        padding: 0 !important;
        margin: 0 0 8px 0 !important;
        word-break: break-word;
    }
    
    .snize-custom-banner .snize-banner-text-sec {
        display: block;
        font-size: 14px;
        text-align: center !important;
        line-height: 1.4;
        color: #64748b;
        padding: 0 !important;
        margin: 0 !important;
        word-break: break-word;
    }
    
    .snize-custom-banner .snize-banner-link {
        display: inline-block !important;
        margin: 8px auto 0 auto !important;
        padding: 12px 24px !important;
        font-size: 14px !important;
        text-align: center !important;
        width: auto !important;
        max-width: 90% !important;
        box-sizing: border-box;
        white-space: nowrap;
        text-decoration: none !important;
    }
    
    .snize-custom-banner .snize-banner-link::after {
        display: none !important;
    }
    
    .snize-custom-banner.multi-link-banner .snize-banner-links-container {
        flex-direction: column;
        width: 100%;
        gap: 10px;
    }
    
    .snize-custom-banner.multi-link-banner .snize-banner-link {
        width: 90% !important;
        max-width: 280px !important;
        margin: 0 auto !important;
    }
}

/* Small mobile styles */
@media (max-width: 480px) {
    .snize-custom-banner {
        padding: 18px 8px !important;
        width: calc(100% - 16px) !important;
    }
    
    .snize-custom-banner .snize-banner-text-main {
        font-size: 16px;
    }
    
    .snize-custom-banner .snize-banner-text-sec {
        font-size: 13px;
    }
    
    .snize-custom-banner .snize-banner-link {
        padding: 11px 20px !important;
        font-size: 13px !important;
    }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
    .snize-custom-banner {
        border: 2px solid #1e293b;
        background: #ffffff !important;
    }
    
    .snize-custom-banner .snize-banner-link {
        border: 2px solid currentColor;
    }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
    .snize-custom-banner,
    .snize-custom-banner .snize-banner-link,
    .snize-custom-banner .snize-banner-link::after,
    .snize-custom-banner .snize-banner-link::before {
        transition: none;
        animation: none;
    }
}

/* Constrain banner width to match collection content */
.snize-custom-banner {
    max-width: 1350px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    margin-top: 40px !important; /* Shift down from header */
    margin-bottom: 36px !important;
    width: calc(100% - 120px) !important; /* Account for page padding */
}

/* Match the collection content padding on desktop */
@media (min-width: 1025px) {
    .snize-custom-banner {
        width: calc(100% - 120px) !important;
        padding-left: 60px;
        padding-right: 60px;
        padding-top: 28px; /* Ensure this stays reduced */
        padding-bottom: 28px; /* Ensure this stays reduced */
    }
}

/* Tablet adjustments */
@media (max-width: 1024px) and (min-width: 769px) {
    .snize-custom-banner {
        width: calc(100% - 64px) !important;
        margin-top: 30px !important;
        padding: 38px 32px 42px 32px;
    }
}

/* Mobile adjustments - keep existing mobile styles */
@media (max-width: 768px) {
    .snize-custom-banner {
        width: calc(100% - 20px) !important;
        margin-top: 20px !important;
        /* Keep all your existing mobile styles */
    }
}
</style>
`;

// BANNER CONFIGURATION
let banners = {
    "/pages/sauna-heater-size-calculator": {
        textMain: "Not Sure Which Sauna Heater Is Best For Your Sauna?",
        textSec: "Answer a few questions and we'll match you with your perfect heater in seconds.",
        button: "Find Your Perfect Heater",
        collections: ["/collections/electric-sauna-heaters", "/collections/sauna-heaters-with-wifi-connectivity", "/collections/wall-mounted-sauna-heaters",]
    },
    "/pages/wood-sauna-stove-sizing-tool-model-recommender": {
        textMain: "Not Sure Which Wood Stove Is Best For Your Sauna?",
        textSec: "Answer a few questions and we'll match you with your perfect stove in seconds.",
        button: "Find Your Perfect Stove",
        collections: ["/collections/wood-burning-sauna-heaters", "/collections/cozy-heat-sauna-stoves", "/collections/narvi"]
    },
    "/pages/finnmark-infrared-sauna-heater-selector-tool": {
        textMain: "Not Sure Which Infrared Sauna Heaters Are Best For Your Sauna?",
        textSec: "Answer a few questions and we'll match you with the perfect heaters in seconds.",
        button: "Find Your Perfect Heaters",
        collections: ["/collections/infrared-sauna-heaters"]
    },
    "/pages/infrared-sauna-selector-tool": {
        textMain: "Not Sure Which Infrared Sauna Is Right For You?",
        textSec: "Answer a few questions and we'll match you with your perfect sauna in seconds.",
        button: "Find Your Perfect Sauna",
        collections: ["/collections/infrared-saunas", "/collections/red-light-therapy-saunas", "/collections/full-spectrum-infrared-saunas", "/collections/far-infrared-saunas", "/collections/residential-infrared-saunas", "/collections/commercial-infrared-saunas", "/collections/low-emf-infrared-saunas", "/collections/ultra-low-emf-infrared-saunas", "/collections/near-zero-emf-infrared-saunas", "/collections/maxxus", "/collections/dynamic-saunas"]
    },
    "/pages/traditional-sauna-selector-tool": {
        textMain: "Not Sure Which Sauna Is Right For You?",
        textSec: "Answer a few questions and we'll match you with your perfect sauna in seconds.",
        button: "Find Your Perfect Sauna",
        collections: ["/collections/traditional-saunas", "/collections/indoor-traditional-saunas", "/collections/outdoor-traditional-saunas", "/collections/electric-saunas", "/collections/wood-burning-saunas", "/collections/hybrid-saunas", "/collections/barrel-saunas", "/collections/log-cabin-saunas", "/collections/cube-saunas", "/collections/pod-shaped-saunas", "/collections/diy-outdoor-sauna-kits", "/collections/pre-assembled-saunas", "/collections/saunas-with-wifi-controls", "/collections/cedar-saunas-for-sale", "/collections/outdoor-saunas", "/collections/dry-saunas", "/collections/traditional-finnish-saunas", "/collections/steam-saunas", "/collections/hybrid-indoor-saunas"]
    }, 
    "/pages/sauna-selector-tool": {
        textMain: "Not Sure Which Sauna Is Right For You?",
        textSec: "Answer a few questions and we'll match you with your perfect sauna in seconds.",
        button: "Find Your Perfect Sauna",
        collections: ["/collections/saunas", "/collections/indoor-saunas", "/collections/1-person-saunas", "/collections/2-person-saunas", "/collections/3-person-saunas", "/collections/4-person-saunas", "/collections/5-person-saunas", "/collections/6-person-saunas", "/collections/7-person-saunas", "/collections/8-person-saunas"]
    },
    "/pages/sauna-door-selector-tool": {
        textMain: "Not Sure Which Sauna Door Is Right For You?",
        textSec: "Answer a few questions and we'll match you with the perfect door in seconds.",
        button: "Find Your Perfect Door",
        collections: ["/collections/sauna-door", "/collections/glass-sauna-doors", "/collections/wood-sauna-doors"]
    },
    "/pages/sauna-wood-calculator-plan-your-custom-sauna-project": {
        textMain: "Building A Sauna? Calculate Your Wood Needs Instantly",
        textSec: "Get the exact quantities you need—no waste, no shortages, no guesswork.",
        button: "Calculate Your Wood Needs",
        collections: ["/collections/sauna-wood","/collections/sauna-woods", "/collections/thermo-aspen-sauna-wood", "/collections/aspen-sauna-wood", "/collections/alder-sauna-wood", "/collections/thermo-radiata-pine-sauna-wood", "/collections/cedar-sauna-wood", "/collections/thermo-spruce-sauna-wood", "/collections/hemlock-sauna-wood", "/collections/thermo-alder-sauna-wood", "/collections/sauna-wood-for-walls", "/collections/sauna-wood-for-benches"]
    },
    "/pages/red-light-therapy-selector-tool": {
        textMain: "Not Sure Which Red Light Panel Is Perfect For You?",
        textSec: "Take our quick quiz and get personalized recommendations based on your wellness goals, space, and budget.",
        button: "Find My Perfect Panel",
        collections: ["/collections/hooga-red-light-therapy", "/collections/red-light-therapy"]
    },
    "multi-heater-tools": {
        textMain: "Not Sure Which Sauna Heater Is Right For You?",
        textSec: "Choose your heater type below and we'll match you with the perfect model in seconds.",
        multiLink: true,
        links: [
            { url: "/pages/sauna-heater-size-calculator", text: "Electric Heaters" },
            { url: "/pages/wood-sauna-stove-sizing-tool-model-recommender", text: "Wood Burning Stoves" },
            { url: "/pages/finnmark-infrared-sauna-heater-selector-tool", text: "Infrared Heaters" }
        ],
        collections: ["/collections/sauna-heaters", "/collections/sauna-heater-packages"]
    }
}

let isEnabled = true;

// Inject styles once
if (!document.getElementById('snize-banner-styles')) {
    const styleElement = document.createElement('div');
    styleElement.id = 'snize-banner-styles';
    styleElement.innerHTML = bannerStyles;
    document.head.appendChild(styleElement);
}

// SETTINGS END. DON'T MODIFY THE CODE BELOW.

document.addEventListener('Searchanise.Loaded', function () {
    (function ($) {

      if (!isEnabled) { return }

        $(document).on('Searchanise.ResultsUpdated', function (event, container) {

            let currentBanner;

            for (let entries of Object.entries(banners)) {
                if (entries[1].collections.includes(location.pathname)) {
                    currentBanner = entries[0];
                }
            }

if (currentBanner && banners[currentBanner] && !$('.snize-custom-banner').length) {
    if (!banners[currentBanner].textSec) {
        banners[currentBanner].textSec = '';
    }
    
    let bannerHTML;
    
    // Check if this is a multi-link banner
    if (banners[currentBanner].multiLink && banners[currentBanner].links) {
        const linksHTML = banners[currentBanner].links.map(link => 
            `<a class="snize-banner-link" href="${link.url}">${link.text}</a>`
        ).join('');
        
        bannerHTML = `
            <div class="snize-custom-banner multi-link-banner">
                <div class="snize-banner-text-container">
                    <span class="snize-banner-text-main">${banners[currentBanner].textMain}</span>
                    <span class="snize-banner-text-sec">${banners[currentBanner].textSec}</span>
                </div>
                <div class="snize-banner-links-container">
                    ${linksHTML}
                </div>
            </div>
        `;
    } else {
        // Original single-link banner
        bannerHTML = `
            <div class="snize-custom-banner">
                <div class="snize-banner-text-container">
                    <span class="snize-banner-text-main">${banners[currentBanner].textMain}</span>
                    <span class="snize-banner-text-sec">${banners[currentBanner].textSec}</span>
                </div>
                <a class="snize-banner-link" href="${currentBanner}">${banners[currentBanner].button}</a>
            </div>
        `;
    }
    
    // Method 1: Try inserting after the Searchanise navigation
    if ($('.searchanise-navigation').length && !$('.searchanise-navigation').next('.snize-custom-banner').length) {
        $('.searchanise-navigation').after(bannerHTML);
    } 
    // Method 2: Try before the snize results container
    else if ($('#snize_results').length && !$('#snize_results').prev('.snize-custom-banner').length) {
        $('#snize_results').before(bannerHTML);
    }
    // Method 3: Original fallback
    else {
        $('.snize-search-results-main-content', container).prepend(bannerHTML);
    }
}
        });
    })(window.Searchanise.$);
});